import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StockCard = ({ 
  symbol, 
  name, 
  price, 
  change, 
  changePercent, 
  volume, 
  isWatchlisted = false,
  onToggleWatchlist,
  onClick
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const isPositive = change >= 0;
  const formattedChange = `${isPositive ? '+' : ''}₹${change.toFixed(2)}`;
  const formattedChangePercent = `${isPositive ? '+' : ''}${changePercent.toFixed(2)}%`;

  const handleWatchlistToggle = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const watchlist = JSON.parse(localStorage.getItem('stockWatchlist') || '[]');
      let updatedWatchlist;
      
      if (isWatchlisted) {
        // Remove from watchlist
        updatedWatchlist = watchlist.filter(stock => stock.symbol !== symbol);
      } else {
        // Add to watchlist
        const stockData = { symbol, name, price, change, changePercent, volume };
        updatedWatchlist = [...watchlist, stockData];
      }
      
      localStorage.setItem('stockWatchlist', JSON.stringify(updatedWatchlist));
      
      if (onToggleWatchlist) {
        onToggleWatchlist(symbol, !isWatchlisted);
      }
      
      setIsLoading(false);
    }, 500);
  };

  const handleStockClick = () => {
    if (onClick) {
      onClick({ symbol, name, price, change, changePercent, volume });
    } else {
      // Navigate to stock search page with this stock pre-selected
      navigate(`/stock-search-discovery?symbol=${symbol}`);
    }
  };

  const handleBuyStock = (e) => {
    e.stopPropagation();
    // Navigate to portfolio management with buy action
    navigate('/portfolio-management', { 
      state: { action: 'buy', symbol, name, price } 
    });
  };

  return (
    <div 
      className="flex items-center justify-between p-4 hover:bg-muted/30 rounded-lg transition-colors cursor-pointer group"
      onClick={handleStockClick}
    >
      <div className="flex items-center space-x-3 flex-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <span className="text-sm font-mono font-semibold text-primary">
              {symbol?.slice(0, 2)}
            </span>
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-semibold text-foreground font-mono">
              {symbol}
            </p>
            {isWatchlisted && (
              <Icon name="Eye" size={12} className="text-secondary" />
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {name}
          </p>
          <p className="text-xs text-muted-foreground">
            Vol: {volume}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <div className="text-right">
          <p className="text-sm font-mono font-semibold text-foreground">
            ${price}
          </p>
          <div className="flex items-center space-x-1">
            <Icon 
              name={isPositive ? "TrendingUp" : "TrendingDown"} 
              size={12} 
              className={isPositive ? "text-success" : "text-destructive"}
            />
            <p className={`text-xs font-mono ${isPositive ? 'text-success' : 'text-destructive'}`}>
              {formattedChange}
            </p>
          </div>
          <p className={`text-xs font-mono ${isPositive ? 'text-success' : 'text-destructive'}`}>
            {formattedChangePercent}
          </p>
        </div>

        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleWatchlistToggle}
            disabled={isLoading}
            className="h-8 w-8"
          >
            <Icon 
              name={isWatchlisted ? "EyeOff" : "Eye"} 
              size={14} 
              className={isWatchlisted ? "text-secondary" : "text-muted-foreground"}
            />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBuyStock}
            className="h-8 w-8"
          >
            <Icon name="Plus" size={14} className="text-success" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StockCard;